import { VueQueryPlugin } from '@tanstack/vue-query';
import App from '@/App.vue';
import i18n from '@/dependencies/i18n';
import pinia from '@/stores';
import Oruga from '@oruga-ui/oruga-next';
import router from '@/router';
import UiIcon from '@/components/Global/UiIcon/UiIcon.vue';
import usePopup from '@/composables/usePopup';
import { createApp } from 'vue';
import { createGtm } from '@gtm-support/vue-gtm';
import MatomoService from '@/services/matomo.service';
import RollbarPlugin from '@/dependencies/rollbar';
import { registerSW } from 'virtual:pwa-register';
import { bulmaConfig } from '@oruga-ui/theme-bulma';
import languageService from '@/services/language.service';
import applicationService from '@/services/application.service';
import { bridgeLocale, setupI18n } from '@/core/i18n';
import { LocalesEnum } from '@/enums/locales.enum';

import '@/helpers/validators';
import '@/composables/useRollbar';
import '@/assets/scss/bulma-base-layer.css';
import '@/assets/scss/styles.scss';
import { useApplicationStore } from '@/stores/application.store';
import { setupPiniaFormDevtools } from './lib/pinia-form/pinia-form.devtools';

if (import.meta.env.MODE !== 'development' && import.meta.env.MODE !== 'e2e') {
    new MatomoService();
}

async function createVue() {
    const app = createApp(App).use(pinia);

    if (import.meta.env.MODE === 'development') {
        setupPiniaFormDevtools(app);
    }

    await applicationService.init();

    app.use(router)
        .use(VueQueryPlugin)
        .use(i18n)
        .use(Oruga, { ...bulmaConfig, iconPack: 'fa', collapse: { ...bulmaConfig.collapse, rootClass: 'o-collapse' } })
        .use(RollbarPlugin);

    // Both i18n solutions run until the app moves to `@/core/i18n`: `languageService` resolves the startup
    // locale and keeps it in vue-i18n; the bridge copies every change to the other side.
    setupI18n({ initialLocale: i18n.global.locale.value });
    bridgeLocale(i18n.global.locale, Object.values(LocalesEnum));

    app.component('UiIcon', UiIcon);
    app.mount('#app');

    languageService.setUserLanguage();

    const appStore = useApplicationStore();
    if (import.meta.env.PROD && import.meta.env.MODE === 'production') {
        const id = appStore.info?.tenant.gtmTag || import.meta.env.VUE_APP_GTM;

        // Only load if GTM code is valid
        if (typeof id === 'string' && /^(GTM|G)-[0-9A-Z]+$/.test(id)) {
            app.use(createGtm({ id }));
        }
    }

    usePopup();
    registerSW({
        immediate: true,
        onRegisteredSW(swUrl, r) {
            setInterval(async () => {
                if (!r) {
                    return;
                }

                if (r.installing || !navigator) {
                    return;
                }

                if ('connection' in navigator && !navigator.onLine) {
                    return;
                }

                const resp = await fetch(swUrl, {
                    cache: 'no-store',
                    headers: {
                        cache: 'no-store',
                        'cache-control': 'no-cache',
                    },
                });

                if (resp?.status === 200 && r.active) {
                    await r.update();
                }
            }, 3600000); // every hour
        },
    });
}

createVue();
